import { SystemNotificationsRequestAccessComponent } from './request-access/request-access.component';
import { Injectable, NgZone, Signal } from '@angular/core';
import { AccessRightType } from '@fi-shared/types/access-right-type';
import { ApplicationSettingsType } from '@fi-shared/types/application-settings-type';
import { DocumentService } from '../document/document.service';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';
import { ChecklistService } from '../checklist/checklist.service';
import { SideListOptionType } from '@fi-shared/types/side-list-option-type';
import { SystemNotificationsCheckpointAnswerComponent } from './checkpoint-answer/checkpoint-answer.component';
import { SystemNotificationsChecklistsComponent } from './checklists/checklists.component';
import { SystemNotificationsAutosavedComponent } from './autosaved/autosaved.component';
import { BehaviorSubject, Subject } from 'rxjs';
import { ChecklistsReminderService } from '@fi-app/system-notifications/checklists/checklists-reminder.service';
import { FiFormTodoService } from '@fi-app/fi-form/fi-form-todo.service';
import { SystemNotificationsFormsComponent } from './forms/forms.component';
import { SystemNotificationsTasksComponent } from '@fi-app/system-notifications/tasks/tasks.component';
import { UserAccessRequestsService } from '@fi-app/user/user-access-requests.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { SystemNotificationsChecklists2Component } from '@fi-app/system-notifications/checklists2/checklists2.component';
import { Checklists2SystemNotificationsService } from '@fi-app/system-notifications/checklists2/checklists2-system-notifications.service';
import { SystemNotificationsAuditChecklists2Component } from '@fi-app/system-notifications/auditChecklists2/audit-checklists2.component';
import { FiTaskSyncService } from '@fi-shared/fi-task-shared/fi-task-sync-service';
import { FiTaskV2Service } from '@fi-app/task-v2/fi-task-v2.service';
import { SystemNotificationsTasks2Component } from '@fi-app/system-notifications/tasks2/tasks2.component';
import { AppTextService } from '@fi-shared/app-text';
import { FiOnboardingActiveDataService } from '@fi-app/fi-onboarding/fi-onboarding-active/fi-onboarding-active-data.service';
import { IComponentRef } from '@fi-app/core/core';
import { FiSystemNotificationsOnboardingsComponent } from '@fi-app/system-notifications/onboardings/fi-system-notifications-onboardings.component';

export interface ISystemNotificationTab {
    countWatch: BehaviorSubject<number>;
    //countWatchSignal?: Signal<number>;
    id: string;
    title: string;
    iconClass: string;
    component: IComponentRef;
    countSref?: string;
    countSrefParams?: () => void;
    mobileButtons?: {
        CssClass;
        onClick;
        isDisabled;
    }[];
    $isEmpty?: boolean;
}

@Injectable()
export class SystemNotificationsService {
    tabs: ISystemNotificationTab[] = [];
    public closing = false;
    systemNotificationsCount$ = new BehaviorSubject<number>(0);

    constructor(
        private documentService: DocumentService,
        private userAccessRequestsService: UserAccessRequestsService,
        private accessToApplicationTypeService: AccessToApplicationTypeService,
        private checklistService: ChecklistService,
        private checklistsReminderService: ChecklistsReminderService,
        private checklists2SystemNotificationsService: Checklists2SystemNotificationsService,
        private formTodoService: FiFormTodoService,
        private fiTaskV2Service: FiTaskV2Service,
        private ngZone: NgZone,
        private taskSyncService: FiTaskSyncService,
        private appTextService: AppTextService,
        private fiOnboardingActiveDataService: FiOnboardingActiveDataService,
    ) {}

    public getTabs() {
        return this.tabs;
    }

    init() {
        this.userAccessRequestsService.initForSystemNotifications();

        if (
            this.accessToApplicationTypeService.HasAccessToApplicationType(
                ApplicationSettingsType.DocumentLocal,
                AccessRightType.Read,
            )
        ) {
            this.documentService.initForSystemNotifications();
        }

        if (
            this.accessToApplicationTypeService.HasAccessToApplicationType(
                ApplicationSettingsType.FormLocal,
                AccessRightType.Read,
            )
        ) {
            this.formTodoService.initForSystemNotifications();
        }

        if (
            this.accessToApplicationTypeService.HasAccessToApplicationType(
                ApplicationSettingsType.Task,
                AccessRightType.Read,
            ) ||
            this.accessToApplicationTypeService.HasAccessToApplicationType(
                ApplicationSettingsType.TaskV2Local,
                AccessRightType.Read,
            )
        ) {
            this.taskSyncService.initForSystemNotifications(this.fiTaskV2Service);
        }

        const hasAccessToCheckLists = this.accessToApplicationTypeService.HasAccessToApplicationType(
            ApplicationSettingsType.ChecklistLocal,
            AccessRightType.Read,
        );
        if (hasAccessToCheckLists) {
            this.checklistsReminderService.initCheckListsForSystemNotifications();
            this.checklistService.initCheckPointAnswersForSystemNotifications();
        }

        const deviationsTab: ISystemNotificationTab = {
            id: 'deviations',
            title: this.appTextService.get('checklist.deviations'),
            iconClass: 'fa fa-exclamation',
            component: SystemNotificationsCheckpointAnswerComponent,
            mobileButtons: [],
            countWatch: new BehaviorSubject<number>(0),
        };
        this.checklistService.checkPointAnswerNotifications$.subscribe((data) =>
            deviationsTab.countWatch.next(data.length),
        );

        const checklists2Tab: ISystemNotificationTab = {
            id: 'checklists2',
            title: this.appTextService.get('checklist.checklists_v2'),
            iconClass: 'far fa-ballot-check',
            component: SystemNotificationsChecklists2Component,
            mobileButtons: [],
            countWatch: new BehaviorSubject<number>(0),
        };
        this.checklists2SystemNotificationsService.notCompletedChecklistsV2Count$.subscribe((count) =>
            checklists2Tab.countWatch.next(count),
        );

        const auditChecklists2Tab: ISystemNotificationTab = {
            id: 'auditChecklists2',
            title: this.appTextService.get('checklist.checklists_audit_v2'),
            iconClass: 'fas fa-person-chalkboard',
            component: SystemNotificationsAuditChecklists2Component,
            mobileButtons: [],
            countWatch: new BehaviorSubject<number>(0),
        };
        this.checklists2SystemNotificationsService.notCompletedChecklistV2AuditCount$.subscribe((count) =>
            auditChecklists2Tab.countWatch.next(count),
        );

        const checklistsTab: ISystemNotificationTab = {
            id: 'checklists',
            title: this.appTextService.get('checklists'),
            iconClass: 'fa fa-check-square-o',
            countSref: FiStateNames.Checklist.List,
            countSrefParams: function () {
                return {
                    filterType: SideListOptionType.CurrentCheckLists,
                };
            },
            component: SystemNotificationsChecklistsComponent,
            mobileButtons: [],
            countWatch: new BehaviorSubject<number>(0),
        };
        this.checklistsReminderService.checkListReminders$.subscribe((data) =>
            checklistsTab.countWatch.next(data.length),
        );

        const formsTab: ISystemNotificationTab = {
            id: 'forms',
            title: this.appTextService.get('form.module_name'),
            iconClass: 'far fa-clipboard-list',
            component: SystemNotificationsFormsComponent,
            mobileButtons: [],
            countWatch: new BehaviorSubject<number>(0),
        };
        this.formTodoService.todoForms$.subscribe((data) => formsTab.countWatch.next(data.length));

        const tasksTab: ISystemNotificationTab = {
            id: 'tasks',
            title: this.appTextService.get('task.tasks'),
            iconClass: 'fas fa-clipboard-check ',
            component: SystemNotificationsTasksComponent,
            mobileButtons: [],
            countWatch: new BehaviorSubject<number>(0),
        };
        if (
            this.accessToApplicationTypeService.HasAccessToApplicationType(
                ApplicationSettingsType.Task,
                AccessRightType.Read,
            )
        ) {
            this.taskSyncService.tasksToBeDoneCount$.subscribe((value) => tasksTab.countWatch.next(value));
        }

        const onboardingTab: ISystemNotificationTab = {
            id: 'onboardings',
            title: this.appTextService.get('onboarding.onboardings'),
            iconClass: 'fa-regular fa-file-certificate ',
            component: FiSystemNotificationsOnboardingsComponent,
            mobileButtons: [],
            //countWatchSignal: this.fiOnboardingActiveDataService.todoOnboardingsCount,
            countWatch: this.fiOnboardingActiveDataService.todoOnboardingsCount$,
        };

        const tasks2Tab: ISystemNotificationTab = {
            id: 'tasks',
            title: this.appTextService.get('task.tasks'),
            iconClass: 'far fa-clipboard-check ',
            component: SystemNotificationsTasks2Component,
            mobileButtons: [],
            countWatch: new BehaviorSubject<number>(0),
        };
        if (
            this.accessToApplicationTypeService.HasAccessToApplicationType(
                ApplicationSettingsType.TaskV2Local,
                AccessRightType.Read,
            )
        ) {
            this.taskSyncService.tasksToBeDoneCount$.subscribe((value) => tasks2Tab.countWatch.next(value));
        }

        const autosavedTab: ISystemNotificationTab = {
            id: 'autosaved',
            title: this.appTextService.get('common.autosaved_tab'),
            iconClass: 'far fa-file-lines',
            component: SystemNotificationsAutosavedComponent,
            countWatch: new BehaviorSubject<number>(0),
        };
        this.documentService.onDraftDocumentsCountChange.subscribe((count) => autosavedTab.countWatch.next(count));

        const accessRequestTab: ISystemNotificationTab = {
            id: 'accessRequests',
            title: this.appTextService.get('requests'),
            iconClass: 'fa fa-user',
            component: SystemNotificationsRequestAccessComponent,
            mobileButtons: [],
            countWatch: new BehaviorSubject<number>(0),
        };
        this.userAccessRequestsService.userAccessRequestsCount$.subscribe((count) =>
            accessRequestTab.countWatch.next(count),
        );

        this.tabs = [
            deviationsTab,
            checklists2Tab,
            auditChecklists2Tab,
            //checklistsTab, //todo remove later code, related to checklists v1
            formsTab,
            onboardingTab,
            tasksTab,
            tasks2Tab,
            autosavedTab,
            accessRequestTab,
        ];

        this.ngZone.run(() => {
            setTimeout(() => {
                this.ringTheBell();
            }, 2000);
        });

        this.tabs.forEach((tab) =>
            tab.countWatch.subscribe((value) => {
                const systemNotificationsCount = this.tabs.reduce((sum, tab) => sum + tab.countWatch.value, 0);
                this.systemNotificationsCount$.next(systemNotificationsCount);
            }),
        );
    }

    ringTheBell() {
        const bellEl = document.getElementById('system_notifications_bell');
        if (!bellEl) {
            return;
        }
        bellEl.classList.remove('animate-bell');
        setTimeout(() => bellEl.classList.add('animate-bell'), 10);
    }

    toggle() {
        if (this.opened) {
            this.close();
        } else {
            this.open();
        }
    }

    open() {
        this.opened = true;
        this.closing = false;
    }

    close() {
        this.opened = false;
        this.closing = true;
    }

    private _opened: boolean = false;
    get opened() {
        return this._opened;
    }

    set opened(value) {
        this._opened = value;
        this.onToggle.next();
    }

    onToggle = new Subject<void>();
}
